import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import PageHero from '../components/PageHero';
import styles from '../styles/AboutUsPage.module.css';

const About = () => {
  return (
    <>
      <PageHero
        backgroundImage="/images/About/hero-bg.svg"
        title="Beyond the Bottom Line"
        subtitle="Crafting Success Stories That Give Back"
        description="At Symbioun, we merge expertise in investment, consulting, and community development to spark growth where it counts. From bold ventures to streamlined business models and thriving communities, we create synergies that do more than succeed—they resonate. Uniting vision with the resources to make it happen, we amplify potential; empowering organisations, uplifting communities, and sparking real change."
        breadcrumb={{
          home: "Home",
          current: "About Us"
        }}
      />
      <section className={styles.aboutPage}>
        <Container>
          <div className={styles.aboutSection}>
            <Row>
              <Col lg={6}>
                <div className={styles.aboutImages}>
                  <div className={styles.image1}>
                    <img src="/images/About/ourstory-2.png" alt="Business Meeting" />
                  </div>
                  <div className={styles.image2}>
                    <img src="/images/About/ourstory-1.png" alt="Business People Working" />
                  </div>
                  <div className={styles.experienceBadge}>
                    <div className="number">6</div>
                    <div className="text">Years Experience</div>
                  </div>
                </div>
              </Col>
              <Col lg={6}>
                <div className={styles.aboutContent}>
                  <div className={styles.sectionBadge}>
                    <div className="lines">
                      <img src="/icons/Line.svg" alt="line" />
                    </div>
                    <span>Know Our Company</span>
                  </div>
                  <h2 className={styles.sectionTitle}>Our Story</h2>
                  <p className={styles.sectionDescription}>
                    Symbioun Limited was established in 2018 with a bold vision: to redefine how investment impacts society. We began as a consultancy for startups and a facilitator for Sri Lanka’s unique exports, but our mission quickly evolved. We realized that true national progress doesn't come from multimillion-dollar top-down investments, but from uplifting the heartbeat of the country—our rural communities and cottage industries.
                  </p>
                  <p className={styles.sectionDescription}>
                    By rejecting traditional high-interest models like micro-financing, we pioneered a "Partnership with Purpose" approach. Through the support of global well-wishers and venture capitalism, we invest directly in the people. From our first success with Enliven Designers—now a thriving Digital Marketing and BPO provider—to our international export arms like Lanka Mega Mart in the USA, we prove that Sri Lankan excellence has no borders.
                  </p>
                  <p className={styles.sectionDescription}>
                    Our journey has been one of learning and refinement. In Mavadivembu, we transitioned from traditional farming leases to a more profound model of empowerment. Today, our focus is clear: we invest in the infrastructure of the future by empowering the women and children of rural communities, ensuring that our impact is both sustainable and generational.
                  </p>
                </div>
              </Col>
            </Row>
          </div>

          <div className={styles.imageGallery}>
            <Row>
              <Col lg={4}>
                <div className={styles.galleryImage}>
                  <img src="/images/About/mission-1.png" alt="Gallery Image 1" />
                </div>
              </Col>
              <Col lg={5}>
                <div className={styles.galleryImage}>
                  <img src="/images/About/mission-2.png" alt="Gallery Image 2" />
                </div>
              </Col>
              <Col lg={3}>
                <div className={styles.galleryImage}>
                  <img src="/images/About/mission-3.png" alt="Gallery Image 3" />
                </div>
              </Col>
            </Row>
          </div>

          <div className={styles.missionSection}>
            <h2 className={styles.missionTitle}>Our Vision</h2>
            <p className={styles.missionDescription}>
              To become the leading catalyst for grassroots economic transformation in Sri Lanka, where every rural community is a hub of global opportunity.
            </p>
            <h2 className={styles.missionTitle}>Our Mission</h2>
            <p className={styles.missionDescription}>
              Symbioun Limited: Redefining investment through community-led ventures. From digital solutions to natural farming, we are dedicated to uplifting Sri Lankan lives and connecting our heritage to the world.
            </p>

            <div className={styles.missionPoints}>
              <Row>
                <Col lg={6}>
                  <div className={styles.missionPoint}>
                    <div className={styles.checkMark}>
                      <img src="/icons/circle-tick.svg" alt="check" />
                    </div>
                    <div className={styles.pointContent}>
                      <h3>Planting Seeds of Opportunity</h3>
                      <p>We work to provide the financial roots for individuals and families to grow their entrepreneurial dreams into thriving realities, nurturing potential from the ground up.</p>
                    </div>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className={styles.missionPoint}>
                    <div className={styles.checkMark}>
                      <img src="/icons/circle-tick.svg" alt="check" />
                    </div>
                    <div className={styles.pointContent}>
                      <h3>Shaping the Foundations of Growth</h3>
                      <p>With the right tools and insights, we aim to help business owners translate their strategies into operational frameworks that drive long-term profitability.</p>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <div className={styles.missionPoint}>
                    <div className={styles.checkMark}>
                      <img src="/icons/circle-tick.svg" alt="check" />
                    </div>
                    <div className={styles.pointContent}>
                      <h3>Mapping the Path to Enduring Success</h3>
                      <p>Through ongoing guidance and recalibration, we seek to help ventures navigate the twists and turns of business growth, strengthening operational frameworks and strategies.</p>
                    </div>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className={styles.missionPoint}>
                    <div className={styles.checkMark}>
                      <img src="/icons/circle-tick.svg" alt="check" />
                    </div>
                    <div className={styles.pointContent}>
                      <h3>Laying the Groundwork for Collective Impact</h3>
                      <p>By fostering connections among business owners and communities, we dedicate ourselves to creating a vibrant network—a fabric of shared experiences, wisdom, and collaboration.</p>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <div className={styles.missionPoint}>
                    <div className={styles.checkMark}>
                      <img src="/icons/circle-tick.svg" alt="check" />
                    </div>
                    <div className={styles.pointContent}>
                      <h3>Driving Cycles of Progress</h3>
                      <p>With a profit-sharing model as our engine, we reinvest success to spark new ventures, turning individual victories into a chain reaction of opportunity.</p>
                    </div>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className={styles.missionPoint}>
                    <div className={styles.checkMark}>
                      <img src="/icons/circle-tick.svg" alt="check" />
                    </div>
                    <div className={styles.pointContent}>
                      <h3>Building Bridges to Tomorrow</h3>
                      <p>We merge empowerment, education, and sustainability, with a vision of creating a legacy where ambition finds its wings, and communities rise together toward shared prosperity.</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          {/* <section className="partners-section">
            <Container>
              <Row className="title-row">
                <Col lg={12}>
                  <div className={styles.sectionBadge}>
                    <div className="lines">
                      <img src="/icons/Line.svg" alt="line" />
                    </div>
                    <span>WHO HELP US</span>
                  </div>
                  <h2 className={styles.sectionTitle}>Our Partners & Donors</h2>
                </Col>
              </Row>
              <Row className="partners-row">
                <Col lg={12}>
                  <div className="partners-container">
                    <div className="partner-image">
                      <img src="/images/Home/Partner-1.svg" alt="Partner 1" />
                    </div>
                    <div className="partner-image">
                      <img src="/images/Home/Partner-2.svg" alt="Partner 2" />
                    </div>
                    <div className="partner-image">
                      <img src="/images/Home/Partner-3.svg" alt="Partner 3" />
                    </div>
                    <div className="partner-image">
                      <img src="/images/Home/Partner-4.svg" alt="Partner 4" />
                    </div>
                    <div className="partner-image">
                      <img src="/images/Home/Partner-5.svg" alt="Partner 5" />
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </section> */}
        </Container>
      </section>
    </>
  );
};

export default About; 